import React, { useCallback, useEffect, useState } from 'react';
import { getBotPromos, createBotPromo, updateBotPromo, deleteBotPromo } from '../../services/api';
import { formatDate } from '../../utils/formatters';
import './BotPromosPanel.css';

// Акции для кнопки «🔥 Акции» в боте гостя. Гость видит только включённые,
// у которых сегодня в сроке; рассылки сюда больше не попадают.

const EMPTY = { title: '', text: '', starts_on: '', ends_on: '' };
const errorOf = (err, fallback) => err?.response?.data?.error || fallback;

const promoState = (p, today) => {
  if (!p.is_active) return { label: 'Выключена', cls: 'st-unknown' };
  if (p.ends_on && p.ends_on < today) return { label: 'Закончилась', cls: 'st-lost' };
  if (p.starts_on && p.starts_on > today) return { label: `С ${formatDate(p.starts_on)}`, cls: 'st-risk' };
  return { label: 'Видна гостям', cls: 'st-active' };
};

const period = (p) => {
  if (p.starts_on && p.ends_on) return `${formatDate(p.starts_on)} — ${formatDate(p.ends_on)}`;
  if (p.ends_on) return `до ${formatDate(p.ends_on)}`;
  if (p.starts_on) return `с ${formatDate(p.starts_on)}`;
  return 'без срока';
};

const PromoForm = ({ initial, limits, onSave, onCancel }) => {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await onSave(form);
    } catch (err) {
      setError(errorOf(err, 'Не удалось сохранить акцию'));
      setBusy(false);
    }
  };

  return (
    <form className="bpromo-form" onSubmit={submit}>
      <div className="ui-field">
        <label>Название</label>
        <input type="text" value={form.title} onChange={set('title')} maxLength={limits.title}
               placeholder="например: Банный вечер по пятницам" autoFocus />
      </div>
      <div className="ui-field">
        <label>Текст для гостя</label>
        <textarea rows={4} value={form.text} onChange={set('text')} maxLength={limits.text}
                  placeholder="Условия акции: что, когда, сколько стоит" />
        <span className="bpromo-counter">{form.text.length} / {limits.text}</span>
      </div>
      <div className="bpromo-dates">
        <div className="ui-field">
          <label>Начало</label>
          <input type="date" value={form.starts_on || ''} onChange={set('starts_on')} />
        </div>
        <div className="ui-field">
          <label>Последний день</label>
          <input type="date" value={form.ends_on || ''} onChange={set('ends_on')} />
        </div>
      </div>
      <p className="bpromo-hint">Без дат акция видна гостям, пока вы её не выключите.</p>
      {error && <div className="ui-inline-error">{error}</div>}
      <div className="bpromo-form-actions">
        <button type="button" className="ui-btn-ghost" onClick={onCancel} disabled={busy}>Отмена</button>
        <button type="submit" className="ui-btn-primary" disabled={busy || !form.title.trim()}>
          {busy ? 'Сохраняю…' : 'Сохранить'}
        </button>
      </div>
    </form>
  );
};

const BotPromosPanel = () => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [error, setError] = useState('');

  const load = useCallback(() => getBotPromos().then(setData)
    .catch((err) => setError(errorOf(err, 'Не удалось загрузить акции'))), []);
  useEffect(() => { load(); }, [load]);

  if (!data) return error ? <div className="panel ui-inline-error">{error}</div> : null;

  const save = async (form) => {
    if (editing === 'new') await createBotPromo(form);
    else await updateBotPromo(editing, form);
    setEditing(null);
    await load();
  };
  const act = async (fn) => {
    setError('');
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errorOf(err, 'Не удалось изменить акцию'));
    }
  };
  const remove = (p) => {
    if (window.confirm(`Удалить акцию «${p.title}»?`)) act(() => deleteBotPromo(p.id));
  };
  const visible = data.promos.filter((p) => promoState(p, data.today).cls === 'st-active').length;

  return (
    <div className="panel bpromo">
      <div className="bpromo-head">
        <div>
          <h3>Акции в боте</h3>
          <p className="bpromo-hint">
            Гость нажимает «🔥 Акции» в боте и видит действующие акции из этого списка.
            {' '}Сейчас видно: <strong>{visible}</strong>.
          </p>
        </div>
        {editing === null && (
          <button type="button" className="ui-btn-primary" onClick={() => setEditing('new')}>Добавить акцию</button>
        )}
      </div>

      {error && <div className="ui-inline-error">{error}</div>}

      {editing === 'new' && (
        <PromoForm initial={EMPTY} limits={data.limits} onSave={save} onCancel={() => setEditing(null)} />
      )}

      {data.promos.length === 0 && editing === null && (
        <p className="bpromo-hint">Акций пока нет — гость увидит «Сейчас действующих акций нет».</p>
      )}

      <div className="bpromo-list">
        {data.promos.map((p) => {
          if (editing === p.id) {
            return (
              <PromoForm key={p.id} limits={data.limits} onSave={save} onCancel={() => setEditing(null)}
                         initial={{ title: p.title, text: p.text, starts_on: p.starts_on || '', ends_on: p.ends_on || '' }} />
            );
          }
          const state = promoState(p, data.today);
          return (
            <div key={p.id} className={`bpromo-item ${state.cls === 'st-active' ? '' : 'is-muted'}`}>
              <div className="bpromo-item-body">
                <div className="bpromo-item-title">
                  <strong>{p.title}</strong>
                  <span className={`gb-badge ${state.cls}`}>{state.label}</span>
                </div>
                {p.text && <div className="bpromo-item-text">{p.text}</div>}
                <div className="bpromo-item-period">{period(p)}</div>
              </div>
              <div className="bpromo-item-actions">
                <label className="bpromo-toggle">
                  <input type="checkbox" checked={p.is_active}
                         onChange={(e) => act(() => updateBotPromo(p.id, { is_active: e.target.checked }))} />
                  Показывать
                </label>
                <button type="button" className="ui-btn-ghost" onClick={() => setEditing(p.id)}
                        disabled={editing !== null}>Изменить</button>
                <button type="button" className="ui-btn-danger" onClick={() => remove(p)}
                        disabled={editing !== null}>Удалить</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BotPromosPanel;
